// src/components/invoice/membership/MemberInvoiceTooltip.tsx
//
// The pop-up that opens when a row in Billing History (or Future Invoices) is
// clicked: "Payment Details" for a payment, "Invoice Details" for a charge.
// For a payment it answers the question staff and families ask (who paid
// this, and how) from utils/paymentOrigin, in plain words. It shows a method
// as a word ("Card", "Cash"), never a card number, bank detail or processor id.
import React from "react";
import { X } from "lucide-react";
import { Button } from "../../ui";
import {
  describePaymentOrigin,
  paymentOriginFields,
  isAccountCreditEntry,
} from "../../../utils/paymentOrigin";
import { formatDateOnly } from "@mediatechgroup/calendar-date";
import { getCheckDetails } from "../../../utils/checkDetails";
import { chargeStatus } from "../../../utils/chargeStatus";
import ChargeStatusBadge from "../../membership/BillingHistory/ChargeStatusBadge";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import {
  getCardBackground,
  getPrimaryText,
  getMutedText,
  getLightBorder,
  getTableRowStyles,
  getPageBackground,
} from "../../../utils/themeUtils";

interface InvoiceTooltipProps {
  invoice: any;
  onEdit?: (invoiceId: string) => void;
  onDelete?: (invoice: any) => void;
  onRefund?: (invoice: any) => void;
  onClose?: () => void;
  isAdminOrStaff?: boolean;
  /**
   * Shared accounts only. Who was responsible for this charge (a name, or the
   * staff dropdown). Billing History shows this here and nowhere else: the
   * table has no column for it (owner, 2026-10-09).
   */
  responsible?: React.ReactNode;
  /** Shared accounts only: the person on the account who made this payment. */
  sharedPaidBy?: string | null;
  /** Shared accounts only: the "one balance" explanation, under Responsible. */
  sharedNote?: React.ReactNode;
}

const MemberInvoiceTooltip: React.FC<InvoiceTooltipProps> = ({
  invoice,
  onEdit,
  onDelete,
  onRefund,
  onClose,
  isAdminOrStaff = false,
  responsible,
  sharedPaidBy,
  sharedNote,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation('membership');

  // Determine if this is a payment (credit) or an invoice
  const isPayment =
    invoice?.metadata?.credit || invoice?.metadata?.type === "credit" || invoice?.metadata?.display_payment_method;
  // An account credit staff added is not a payment: no receipt, no card refund.
  const accountCredit = !!isPayment && isAccountCreditEntry(invoice);
  const itemType = accountCredit
    ? t('billing.invoiceModal.accountCreditDetails', 'Account credit')
    : isPayment
      ? t('billing.invoiceModal.paymentDetails')
      : t('billing.invoiceModal.invoiceDetails');
  const canRefund = !!onRefund && !!isPayment && !accountCredit;

  // Helper function to translate payment methods
  const translatePaymentMethod = (method: string): string => {
    const methodMap: { [key: string]: string } = {
      'Cash': t('billing.paymentMethods.cash'),
      'Card': t('billing.paymentMethods.card'),
      'Check': t('billing.paymentMethods.check'),
      'Cheque': t('billing.paymentMethods.cheque'),
      'Bank Account': t('billing.paymentMethods.bankAccount'),
      'Bank Account (ACH)': t('billing.paymentMethods.bankAccountACH'),
      'ACH': t('billing.paymentMethods.ach'),
    };
    return methodMap[method] || method;
  };

  // Helper function to translate invoice types
  const translateInvoiceType = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      'Membership Dues': t('billing.invoiceTypes.membershipDues'),
      'Manual Invoice': t('billing.invoiceTypes.manualInvoice'),
    };
    return typeMap[type] || type;
  };

  // Helper function to translate common invoice description patterns
  const translateDescription = (description: string): string => {
    if (!description) return description;
    const invoiceNumberMatch = description.match(/^Invoice #(\d+)$/i);
    if (invoiceNumberMatch) {
      return t('billing.invoiceTypes.invoiceNumber', { number: invoiceNumberMatch[1] });
    }
    const descriptionMap: { [key: string]: string } = {
      'Manual invoice from staff': t('billing.invoiceTypes.manualInvoiceFromStaff'),
    };
    return descriptionMap[description] || description;
  };

  // Helper function to get payment method badge color
  const getPaymentMethodColor = (method: string): string => {
    const colors: { [key: string]: string } = {
      'Cash': isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800',
      'Card': isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800',
      'Check': isDarkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
      'Cheque': isDarkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
      'Bank Account': isDarkMode ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-100 text-indigo-800',
      'Bank Account (ACH)': isDarkMode ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-100 text-indigo-800',
      'ACH': isDarkMode ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-100 text-indigo-800',
    };
    return colors[method] || (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800');
  };


  // Get line items from invoice metadata
  // For Membership Dues: invoice.metadata.calculateData.slotBreakdown
  // For Manual Invoices: invoice.metadata.lineItems or invoice.lineItems
  const slotBreakdown = invoice?.metadata?.calculateData?.slotBreakdown || [];
  const editableAmounts = invoice?.metadata?.editableAmounts || {};
  const manualLineItems = invoice?.metadata?.lineItems || invoice?.lineItems || [];

  const hasMembershipLineItems = Array.isArray(slotBreakdown) && slotBreakdown.length > 0;
  const hasManualLineItems = Array.isArray(manualLineItems) && manualLineItems.length > 0;
  const hasLineItems = hasMembershipLineItems || hasManualLineItems;

  // Calculate line item total for manual invoice items
  const calculateManualLineTotal = (item: any) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.unitPrice) || 0;
    const discount = parseFloat(item.discount) || 0;
    return qty * price - discount;
  };

  // Detect a discrepancy between the invoice amount and the manual line items
  // total. When this is non-zero we render a warning banner so the user is
  // never confused by the click-to-expand panel showing $0 while the row
  // total shows $10 (or vice versa). Membership-dues invoices have their own
  // breakdown semantics and aren't checked here.
  const manualLineItemsTotal = hasManualLineItems
    ? manualLineItems.reduce((sum: number, item: any) => sum + calculateManualLineTotal(item), 0)
    : 0;
  const invoiceAmountNum = parseFloat(invoice?.amount) || 0;
  const lineItemAmountMismatch =
    hasManualLineItems && Math.abs(manualLineItemsTotal - invoiceAmountNum) > 0.01;

  const checkDetails = getCheckDetails(invoice?.metadata);
  const isCheck =
    invoice?.metadata?.display_payment_method === "Check" ||
    invoice?.metadata?.display_payment_method === "Cheque" ||
    (isPayment && !!checkDetails.checkNumber);
  const origin = isPayment ? describePaymentOrigin(invoice, { viewerIsStaff: isAdminOrStaff }) : null;
  const memberName =
    [invoice?.membership?.first_name, invoice?.membership?.last_name].filter(Boolean).join(" ") || "—";
  const amountNumber = Number(invoice?.amount);
  const amountText = Number.isFinite(amountNumber) ? `$${amountNumber.toFixed(2)}` : "—";
  // Who created a charge is staff-only context (an invoice, not a payment).
  const createdBy =
    !isPayment && isAdminOrStaff && invoice?.processed_by?.name
      ? `${invoice.processed_by.name}${invoice.processed_by.role ? ` (${invoice.processed_by.role})` : ""}`
      : null;

  // Each part of the pop-up sits in its own lightly bordered box, and the
  // whole pop-up is narrower with tighter spacing (owner walkthrough
  // 2026-10-07: "shrink the whole size", "pretty borders around each section").
  const sectionClass = `rounded-md border ${getLightBorder(isDarkMode)} p-2.5`;
  const labelClass = `text-xs font-semibold ${getMutedText(isDarkMode)} uppercase tracking-wide`;
  const valueClass = `${getPrimaryText(isDarkMode)} font-medium break-words`;
  const field = (label: string, value: React.ReactNode, wide = false) => (
    <div className={wide ? "sm:col-span-2" : ""}>
      <dt className={labelClass}>{label}</dt>
      <dd className={valueClass}>{value}</dd>
    </div>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="billing-details-title"
      className={`fixed z-50 ${getCardBackground(
        isDarkMode
      )} text-sm ${getPrimaryText(isDarkMode)} border ${getLightBorder(
        isDarkMode
      )} rounded-lg shadow-xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[480px] max-w-[94vw] max-h-[85vh] flex flex-col`}
    >
      {/* Header: title + close, then the actions on their own wrapping row so
          nothing is squeezed at phone width. */}
      <div className={`px-3 sm:px-4 pt-3 pb-2 border-b ${getLightBorder(isDarkMode)}`}>
        <div className="flex items-start justify-between gap-3">
          <h3 id="billing-details-title" className={`text-base font-semibold ${getPrimaryText(isDarkMode)}`}>
            {itemType}
          </h3>
          {onClose && (
            <Button
              type="button"
              variant="neutral"
              size="sm"
              iconOnly
              aria-label="Close"
              onClick={onClose}
              icon={<X className="w-4 h-4" aria-hidden="true" />}
            />
          )}
        </div>
        {(onEdit || onDelete || canRefund) && (
          <div className="flex flex-wrap gap-2 mt-2">
            {onEdit && (
              <Button type="button" variant="primary" size="sm" onClick={() => onEdit(invoice?.id)}>
                {t('billing.invoiceModal.edit')}
              </Button>
            )}
            {canRefund && onRefund && (
              <Button type="button" variant="secondary" size="sm" onClick={() => onRefund(invoice)}>
                {t('billing.invoiceModal.refund', 'Issue Refund')}
              </Button>
            )}
            {onDelete && (
              <Button type="button" variant="danger" size="sm" onClick={() => onDelete(invoice)}>
                {t('billing.invoiceModal.delete')}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="overflow-auto flex-1 px-3 sm:px-4 py-3 space-y-2">
        {/* What it is, and how much */}
        <div
          data-testid="details-section"
          className={`${sectionClass} flex flex-wrap items-start justify-between gap-x-4 gap-y-2`}
        >
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            {invoice?.metadata?.display_payment_method && (
              <>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getPaymentMethodColor(invoice.metadata.display_payment_method)}`}>
                  {accountCredit
                    ? t('billing.invoiceModal.accountCreditDetails', 'Account credit')
                    : translatePaymentMethod(invoice.metadata.display_payment_method)}
                </span>
                <span className={valueClass}>
                  {translateDescription(invoice?.description) || t('billing.invoiceTypes.payment')}
                </span>
              </>
            )}
            {invoice?.metadata?.display_invoice_type && (
              <>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                  {t('billing.invoice')}
                </span>
                <span className={valueClass}>
                  {translateInvoiceType(invoice.metadata.display_invoice_type)}
                </span>
              </>
            )}
            {!invoice?.metadata?.display_payment_method && !invoice?.metadata?.display_invoice_type && (
              <span className={valueClass}>
                {translateDescription(invoice?.description) ||
                  (isPayment ? t('billing.invoiceTypes.payment') : t('billing.invoice'))}
              </span>
            )}
          </div>
          <span
            className={`text-lg font-bold whitespace-nowrap ${
              isPayment ? "text-green-600" : getPrimaryText(isDarkMode)
            }`}
          >
            {amountText}
          </span>
        </div>

        {/* Who paid, and how: payments only */}
        {origin && (
          <div
            data-testid="payment-origin"
            className={`${sectionClass} ${getPageBackground(isDarkMode)}`}
          >
            <p className={`text-sm font-semibold ${getPrimaryText(isDarkMode)}`}>{origin.summary}</p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mt-1.5">
              {paymentOriginFields(origin, { viewerIsStaff: isAdminOrStaff, sharedPaidBy }).map(({ field: key, value }) => (
                <React.Fragment key={key}>
                  {field(
                    key === "paidBy"
                      ? t('billing.invoiceModal.paidBy', 'Paid by')
                      : key === "takenBy"
                        ? t('billing.invoiceModal.takenBy', 'Taken by')
                        : key === "addedBy"
                          ? t('billing.invoiceModal.addedBy', 'Added by')
                          : t('billing.invoiceModal.paymentMethod', 'Payment method'),
                    value
                  )}
                </React.Fragment>
              ))}
            </dl>
          </div>
        )}

        {/* Whose turn it was: charges on a shared account only */}
        {!isPayment && responsible && (
          <div data-testid="shared-account-section" className={sectionClass}>
            <dl className="grid grid-cols-1 gap-y-1.5">
              {field(t('billing.invoiceModal.responsible', 'Responsible'), responsible)}
            </dl>
            {sharedNote && <div className="mt-2">{sharedNote}</div>}
          </div>
        )}

        <dl
          data-testid="details-section"
          className={`${sectionClass} grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2`}
        >
          {field(t('billing.invoiceModal.member'), memberName)}
          {/* Draft / Due / Paid / Canceled ...: the pop-up showed no status. */}
          {!isPayment &&
            chargeStatus(invoice) &&
            field(
              t('billing.invoiceModal.status', 'Status'),
              <ChargeStatusBadge invoice={invoice} isDarkMode={isDarkMode} />
            )}
          {field(
            accountCredit
              ? t('billing.invoiceModal.dateAdded', 'Date added')
              : isPayment
                ? t('billing.invoiceModal.paymentDate', 'Payment date')
                : t('billing.invoiceModal.dueDate'),
            formatDateOnly(invoice?.due_date)
          )}
          {isCheck &&
            field(
              t('billing.invoiceModal.checkNumber'),
              checkDetails.checkNumber || t('billing.invoiceModal.notAvailable')
            )}
          {isCheck &&
            field(
              t('billing.invoiceModal.checkDate'),
              checkDetails.checkDate
                ? formatDateOnly(checkDetails.checkDate)
                : t('billing.invoiceModal.notAvailable')
            )}
          {invoice?.metadata?.display_payment_method &&
            !accountCredit &&
            field(
              t('billing.invoiceModal.receiptNumber'),
              invoice?.metadata?.receipt_number || t('billing.invoiceModal.notAvailable')
            )}
          {createdBy && field(t('billing.invoiceModal.processedBy'), createdBy)}
          {field(
            t('billing.invoiceModal.notes'),
            invoice?.metadata?.note || t('billing.invoiceModal.none'),
            true
          )}
        </dl>

      {/* Line Items Table */}
      {hasLineItems && (
        <div data-testid="details-section" className={sectionClass}>
          <h4 className={`text-sm font-bold ${getPrimaryText(isDarkMode)} mb-2`}>{t('billing.invoiceModal.lineItems')}</h4>
          {lineItemAmountMismatch && (
            <div
              className={`mb-2 p-2 text-xs rounded border ${
                isDarkMode
                  ? "bg-yellow-900/20 border-yellow-700 text-yellow-200"
                  : "bg-yellow-50 border-yellow-300 text-yellow-800"
              }`}
            >
              <strong>Heads up:</strong> the line items below total{" "}
              <strong>${manualLineItemsTotal.toFixed(2)}</strong>, but the invoice amount on
              record is <strong>${invoiceAmountNum.toFixed(2)}</strong>. The invoice amount is
              what affects the member's balance and billing history. Edit the invoice (or
              delete and recreate) to bring them back in sync.
            </div>
          )}
          <div className="overflow-x-auto">
          <table className={`w-full border ${getLightBorder(isDarkMode)} border-collapse text-xs`}>
            <thead>
              <tr className={`${getTableRowStyles(isDarkMode).header} text-xs`}>
                <th className={`border ${getLightBorder(isDarkMode)} px-2 py-1 text-left`}>{t('billing.invoiceModal.description')}</th>
                <th className={`border ${getLightBorder(isDarkMode)} px-2 py-1 text-right`}>{t('billing.invoiceModal.amount')}</th>
                <th className={`border ${getLightBorder(isDarkMode)} px-2 py-1 text-right`}>{t('billing.invoiceModal.adjusted')}</th>
              </tr>
            </thead>
            <tbody>
              {/* Membership Dues */}
              {hasMembershipLineItems && (() => {
                const groups = new Map();
                slotBreakdown.forEach((slot: any) => {
                  const athleteId = slot?.athleteId || slot?.summaryForAthlete || slot?.discountForAthlete;
                  if (!groups.has(athleteId)) groups.set(athleteId, []);
                  groups.get(athleteId).push(slot);
                });

                const rows: JSX.Element[] = [];

                groups.forEach((entries) => {
                  const summary = entries.find((s: any) => s?.summaryForAthlete);
                  const programs = entries.filter((p: any) => !p.summaryForAthlete && !p.discountForAthlete);
                  const discounts = entries.filter((d: any) => d.discountForAthlete);

                  // Program Rows
                  programs.forEach((prog: any) => {
                    const rowKey = `program-${prog?.slotId}-${prog?.athleteId}`;
                    const originalAmount = prog?.amount ?? 0;
                    const adjustedAmount = editableAmounts[rowKey] !== undefined ? editableAmounts[rowKey] : originalAmount;

                    rows.push(
                      <tr key={rowKey} className={getTableRowStyles(isDarkMode).row}>
                        <td className={`border ${getLightBorder(isDarkMode)} px-2 py-1.5`}>
                          <div>{prog?.athleteName} - {prog?.programName}</div>
                        </td>
                        <td className={`border ${getLightBorder(isDarkMode)} px-2 py-1.5 text-right`}>
                          ${originalAmount.toFixed(2)}
                        </td>
                        <td className={`border ${getLightBorder(isDarkMode)} px-2 py-1.5 text-right font-medium`}>
                          ${adjustedAmount.toFixed(2)}
                        </td>
                      </tr>
                    );
                  });

                  // Athlete Total Row
                  if (summary) {
                    const originalAthleteTotal = programs.reduce((sum: number, prog: any) => sum + (prog?.amount ?? 0), 0);
                    const adjustedAthleteTotal = programs.reduce((sum: number, prog: any) => {
                      const rowKey = `program-${prog?.slotId}-${prog?.athleteId}`;
                      return sum + (editableAmounts[rowKey] !== undefined ? editableAmounts[rowKey] : prog?.amount ?? 0);
                    }, 0);

                    const totalHours = programs.reduce((sum: number, prog: any) => sum + (prog?.weeklyHours ?? 0), 0);
                    const tierPrice = (summary as any)?.tierPrice ?? 0;
                    const offset = originalAthleteTotal - tierPrice;
                    const offsetAdjustedTotal = adjustedAthleteTotal - offset;

                    rows.push(
                      <tr key={`summary-${summary?.summaryForAthlete}`} className={`${getTableRowStyles(isDarkMode).header} font-semibold`}>
                        <td className={`border ${getLightBorder(isDarkMode)} px-2 py-1.5`}>
                          {summary?.athleteName} - {t('billing.invoiceModal.monthlyTotals')} ({t('billing.invoiceModal.hourRate', { hours: totalHours.toFixed(1) })})
                        </td>
                        <td className={`border ${getLightBorder(isDarkMode)} px-2 py-1.5 text-right`}>
                          <span className="text-red-600 font-bold line-through">${originalAthleteTotal.toFixed(2)}</span>
                          <span className="ml-1">${tierPrice.toFixed(2)}</span>
                        </td>
                        <td className={`border ${getLightBorder(isDarkMode)} px-2 py-1.5 text-right font-medium`}>
                          <span className="text-red-600 font-bold line-through">${adjustedAthleteTotal.toFixed(2)}</span>
                          <span className="ml-1">${offsetAdjustedTotal.toFixed(2)}</span>
                        </td>
                      </tr>
                    );
                  }

                  // Discount Rows
                  discounts.forEach((discount: any) => {
                    const discountKey = `discount-${discount.discountForAthlete}`;
                    const discountAmount = editableAmounts[discountKey] !== undefined ? editableAmounts[discountKey] : discount?.discountAmount ?? 0;

                    rows.push(
                      <tr key={discountKey} className={`${getTableRowStyles(isDarkMode).row} bg-yellow-50 ${isDarkMode ? 'bg-yellow-900/20' : ''}`}>
                        <td colSpan={2} className={`border ${getLightBorder(isDarkMode)} px-2 py-1.5 font-semibold text-orange-700 ${isDarkMode ? 'text-orange-400' : ''}`}>
                          - ${discountAmount.toFixed(2)} {t('billing.invoiceModal.discount')} ({discount?.note || t('billing.invoiceModal.siblingDiscount')})
                        </td>
                        <td className={`border ${getLightBorder(isDarkMode)} px-2 py-1.5 text-right font-medium`}>
                          ${discountAmount.toFixed(2)}
                        </td>
                      </tr>
                    );
                  });
                });

                // Invoice Total Row
                rows.push(
                  <tr key="invoice-total" className="font-bold bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white">
                    <td className={`border ${getLightBorder(isDarkMode)} px-2 py-2`}>{t('billing.invoiceModal.invoiceTotal')}</td>
                    <td className={`border ${getLightBorder(isDarkMode)} px-2 py-2 text-right`}>
                      <span className="text-red-600 font-bold line-through">${invoice?.metadata?.calculateData?.totalAmount?.toFixed(2) || '0.00'}</span>
                    </td>
                    <td className={`border ${getLightBorder(isDarkMode)} px-2 py-2 text-right font-bold`}>
                      ${invoice?.amount?.toFixed(2)}
                    </td>
                  </tr>
                );

                return rows;
              })()}

              {/* Manual Invoice - Line Items */}
              {hasManualLineItems && manualLineItems.map((item: any, index: number) => (
                <tr key={item.id || index} className={getTableRowStyles(isDarkMode).row}>
                  <td colSpan={2} className={`border ${getLightBorder(isDarkMode)} px-2 py-2`}>
                    <div>{item.description || t('billing.invoiceModal.noDescription')}</div>
                    <div className={`text-xs ${getMutedText(isDarkMode)}`}>
                      {t('billing.invoiceModal.quantity')}: {item.quantity || 1} × ${parseFloat(item.unitPrice || 0).toFixed(2)}
                      {item.discount > 0 && ` - ${t('billing.invoiceModal.discount')}: $${parseFloat(item.discount || 0).toFixed(2)}`}
                    </div>
                  </td>
                  <td className={`border ${getLightBorder(isDarkMode)} px-2 py-2 text-right font-medium`}>${calculateManualLineTotal(item).toFixed(2)}</td>
                </tr>
              ))}
              {/* Manual Invoice Total */}
              {hasManualLineItems && (
                <tr className={`${getTableRowStyles(isDarkMode).header} font-bold`}>
                  <td colSpan={2} className={`border ${getLightBorder(isDarkMode)} px-2 py-2 text-right`}>{t('billing.invoiceModal.total')}:</td>
                  <td className={`border ${getLightBorder(isDarkMode)} px-2 py-2 text-right`}>${manualLineItems.reduce((sum: number, item: any) => sum + calculateManualLineTotal(item), 0).toFixed(2)}</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default MemberInvoiceTooltip;
